import React, { useMemo, useCallback, useState, useEffect, useRef } from 'react'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { GridOptions, GridReadyEvent, GridApi } from 'ag-grid-enterprise'
import { Button } from 'antd'
import { DownloadOutlined } from '@ant-design/icons'
import { GridToolbar } from '../../../shared/components/GridToolbar'
import CustomAgGrid from '../../../shared/components/custom/Grid/CustomAgGrid'
import { useDriverDataColumns } from '../hooks/useDriverDataColumns'
import { DriverData, EnhancedDriverData, FormulaMonthlyBudget } from '../types/driverData'
import { createExcelStyles } from '../../../shared/utils/excelStyles'
import { useBudgetAllocation } from '../../budgetAllocation'
import { convertToFormulaData } from '../utils/convertToFormulaData'
import useUnsavedChangesBlocker from '../../../shared/hooks/useUnsavedChangesBlocker'
import { enhanceDriverData } from '../utils/dataEnhancement'
import { useGridDataActions } from '../../../shared/hooks/useGridDataActions'
import { useDriverDataExcelExport } from '../hooks/useDriverDataExcelExport'
import { contextCompanyIdSelector } from '../../../redux/context/company/selectors'
import { filtersSelector } from '../../../redux/context/filters/selectors'
import { useGridContext } from '../../../shared/hooks/useGridContext'
import { setRegistryValue, getChangesRegistryKey } from '../../../shared/utils/balanceRegistry'
import '../../financialStatements/components/StatementTable/styles.css'

export interface InitialFilters {
  dimensionIds?: string[]
  formulaIds?: number[]
}

interface DriverDataTableFlatProps {
  data: DriverData[]
  isLoading: boolean
  initialFilters?: InitialFilters
  onGridReady?: (api: GridApi<EnhancedDriverData>) => void
  onSave?: (formulaData: FormulaMonthlyBudget[]) => Promise<void>
  toolbarLeftContent?: React.ReactNode
}

const DriverDataTable: React.FC<DriverDataTableFlatProps> = ({
  data,
  isLoading,
  initialFilters,
  onGridReady: onGridReadyProp,
  onSave,
  toolbarLeftContent
}) => {
  const { t } = useTranslation()
  const gridApiRef = useRef<GridApi<EnhancedDriverData>>()
  const [isGridReady, setIsGridReady] = useState(false)
  const [isEditMode, setIsEditMode] = useState(false)
  const companyId = useSelector(contextCompanyIdSelector)
  const { budgetingScenario } = useSelector(filtersSelector)
  const isScenarioLocked = budgetingScenario?.isLocked === true
  const { columnDefs, columns, budgetDriverMap, dimensionMap } = useDriverDataColumns()

  // Create grid context with isEditMode
  const gridContext = useGridContext({
    gridApi: gridApiRef.current,
    isEditMode
  })

  // Enhance driver data with budget driver and dimension names
  const enhancedData = useMemo(
    () => enhanceDriverData(data, budgetDriverMap, dimensionMap),
    [data, budgetDriverMap, dimensionMap]
  )

  // Driver data actions (discard changes, etc.)
  const { hasUnsavedChanges, discardChanges, changedRowsCount } = useGridDataActions({
    gridApi: gridApiRef.current
  })

  // Force edit mode off if scenario becomes locked
  useEffect(() => {
    if (isEditMode && isScenarioLocked) {
      setIsEditMode(false)
      discardChanges()
    }
  }, [isScenarioLocked, isEditMode, discardChanges])

  // Budget allocation hook - handles modal and save flow
  const { AllocationModal, collectChangedRows, hasGroupChanges, setIsModalOpen } = useBudgetAllocation({
    gridApi: gridApiRef.current,
    columns,
    entityType: 'formula',
    onSave: async (budgetData: any) => {
      // budgetData is already FormulaMonthlyBudget[] from entity-specific converter
      await onSave?.(budgetData)
      setIsEditMode(false)
      discardChanges()
    }
  })

  const handleSave = async () => {
    // Stop editing to save the currently active cell
    gridApiRef.current?.stopEditing()

    const changedRows = collectChangedRows()

    if (changedRows.length === 0) {
      console.log('No changes to save')
      return
    }

    if (hasGroupChanges(changedRows)) {
      // Group data → open modal for allocation
      setIsModalOpen(true)
      return // Keep edit mode active
    }

    // Only elements data → direct save
    const formulaData = convertToFormulaData({ rowData: changedRows })
    await onSave?.(formulaData)
    setIsEditMode(false)
    discardChanges()
  }

  // Block navigation when there are unsaved changes
  useUnsavedChangesBlocker({
    hasUnsavedChanges,
    onDiscard: discardChanges
  })

  // Reset filters when company changes
  useEffect(() => {
    if (!gridApiRef.current || !companyId) return

    // Clear all filters when company changes
    gridApiRef.current.setFilterModel(null)

    // Reapply initial filters if provided
    if (initialFilters) {
      const { dimensionIds, formulaIds } = initialFilters
      const filterModel: Record<string, any> = {}

      if (dimensionIds && dimensionIds.length > 0) {
        filterModel.dimensionId = {
          filterType: 'set',
          values: dimensionIds
        }
      }

      if (formulaIds && formulaIds.length > 0) {
        filterModel.formulaId = {
          filterType: 'set',
          values: formulaIds
        }
      }

      if (Object.keys(filterModel).length > 0) {
        gridApiRef.current.setFilterModel(filterModel)
      }
    }
  }, [companyId, initialFilters])

  const onExcelExport = useDriverDataExcelExport(gridApiRef.current)
  const excelStyles = useMemo(() => createExcelStyles(), [])

  const onGridReady = useCallback(
    (event: GridReadyEvent<EnhancedDriverData>) => {
      // Store grid API
      gridApiRef.current = event.api
      setIsGridReady(true)

      // Notify parent component
      onGridReadyProp?.(event.api)

      // Apply initial filters when grid is ready
      if (initialFilters) {
        const { dimensionIds, formulaIds } = initialFilters
        const filterModel: Record<string, any> = {}

        // Set dimension filter
        if (dimensionIds && dimensionIds.length > 0) {
          filterModel.dimensionId = {
            filterType: 'set',
            values: dimensionIds
          }
        }

        // Set formula filter
        if (formulaIds && formulaIds.length > 0) {
          filterModel.formulaId = {
            filterType: 'set',
            values: formulaIds
          }
        }

        // Apply all filters at once
        if (Object.keys(filterModel).length > 0) {
          event.api.setFilterModel(filterModel)
        }
      }
    },
    [initialFilters, onGridReadyProp]
  )

  // Handle cell editing stopped on group columns (dataSource === 'groups') - force value to be
  // stored even if unchanged. This ensures these rows are included in the budget allocation modal
  // even when the user confirms the editor without changing the value.
  const onCellEditingStopped = useCallback((event: any) => {
    if (!event.valueChanged && event.newValue !== undefined && event.data && event.column) {
      const columnDef = event.column.getColDef()
      const columnData = columnDef.headerComponentParams?.columnData
      if (columnData && columnData.dataKey !== undefined && columnData.dataSource === 'groups') {
        const registryKey = columnData.dataType === 'actuals' ? 'actuals' : 'budget'
        const changesRegistryKey = getChangesRegistryKey(registryKey)
        setRegistryValue(event.data, columnData, event.newValue, changesRegistryKey)
        event.api.refreshCells({ rowNodes: [event.node], force: true })
      }
    }
  }, [])

  const statusBar = useMemo(
    () => ({
      statusPanels: [
        { statusPanel: 'agTotalAndFilteredRowCountComponent', align: 'left' as const },
        { statusPanel: 'agSelectedRowCountComponent' },
        { statusPanel: 'agAggregationComponent' }
      ]
    }),
    []
  )

  const sideBar = useMemo(
    () => ({
      toolPanels: [
        {
          id: 'columns',
          labelDefault: 'Columns',
          labelKey: 'columns',
          iconKey: 'columns',
          toolPanel: 'agColumnsToolPanel',
          toolPanelParams: {
            suppressRowGroups: true,
            suppressValues: true,
            suppressPivots: true,
            suppressPivotMode: true
          }
        }
      ],
      defaultToolPanel: ''
    }),
    []
  )

  const cellSelection = useMemo(
    () => ({
      handle: {
        mode: 'fill' as const
      }
    }),
    []
  )

  const gridOptions = useMemo(
    (): GridOptions<EnhancedDriverData> => ({
      columnDefs,
      defaultColDef: {
        resizable: true,
        rowDrag: false,
        sortable: false,
        suppressHeaderMenuButton: true,
        wrapHeaderText: true,
        enableCellChangeFlash: false
      },
      includeHiddenColumnsInQuickFilter: true,
      hidePaddedHeaderRows: true,
      cellSelection,
      enableFilterHandlers: true,
      getRowId: params => `${params.data.formulaId}-${params.data.dimensionId}`,
      rowGroupPanelShow: 'always',
      groupDisplayType: 'groupRows',
      statusBar,
      sideBar,
      // Enable undo/redo for cell editing
      undoRedoCellEditing: true,
      undoRedoCellEditingLimit: 20,
      // Disable row animations to prevent visual flickering during updates
      animateRows: false
    }),
    [columnDefs, statusBar, sideBar, cellSelection]
  )

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', height: '100%' }}>
      <GridToolbar>
        <GridToolbar.Actions align="space-between">
          <div>{toolbarLeftContent}</div>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <GridToolbar.Actions>
              <Button onClick={onExcelExport} icon={<DownloadOutlined />}>
                {t('financialStatementsPage:excel')}
              </Button>
            </GridToolbar.Actions>
            <GridToolbar.EditModeActions
              gridApi={isGridReady ? gridApiRef.current : undefined}
              isEditMode={isEditMode}
              onEnterEditMode={() => setIsEditMode(true)}
              onExitEditMode={() => setIsEditMode(false)}
              changedRowsCount={changedRowsCount}
              onDiscard={discardChanges}
              onSave={handleSave}
              primaryEdit
              disabled={isScenarioLocked}
              disabledTooltip={t('global:isLocked')}
            />
          </div>
        </GridToolbar.Actions>
      </GridToolbar>
      <div style={{ flex: 1 }}>
        <CustomAgGrid
          {...gridOptions}
          rowData={enhancedData}
          loading={isLoading}
          context={gridContext}
          excelStyles={excelStyles}
          onGridReady={onGridReady}
          onCellEditingStopped={onCellEditingStopped}
        />
      </div>

      {/* Budget allocation modal - managed by useBudgetAllocation hook */}
      {AllocationModal}
    </div>
  )
}

export default DriverDataTable
